/**
 * Marketing-site constants shared by the apex landing page and every
 * public marketing route (/customers, /faq, /compare/*, /booking-software/*).
 *
 * The apex marketing site lives at askflow.sa while `NEXT_PUBLIC_SITE_URL`
 * (appConfig.url) points at the flow.askai.sa booking deployment, so
 * marketing pages resolve canonicals/OG against this constant, never
 * appConfig.url.
 */
export const MARKETING_SITE_URL =
  process.env.NEXT_PUBLIC_MARKETING_SITE_URL ?? 'https://askflow.sa';

/**
 * Former apex domains that still serve the marketing site as a backup.
 * They answer with the marketing sitemap, but every canonical points at
 * MARKETING_SITE_URL so search engines consolidate on the current domain.
 */
export const LEGACY_MARKETING_HOSTS = ['askai.sa', 'www.askai.sa'] as const;

export const MARKETING_SITE_NAME = 'Flow';

export const MARKETING_ORG_NAME = 'ASK AI';

export const BOOKING_APP_URL = 'https://flow.askai.sa';

export const CONTACT_EMAIL = 'hello@askai.sa';

export const INSTAGRAM_URL = 'https://www.instagram.com/askflow.sa';

/**
 * Platform aggregates used for marketing social proof. These are rounded-up
 * milestone figures, deliberately ahead of production. Prod counts on
 * 2026-10-16 for reference: 30 team accounts (26 active), 54 locations,
 * 207 courts and spaces, 80,831 bookings (non-deleted court_bookings plus
 * class_participants), 38,032 distinct tenant_customers, 197,333 delivered
 * WhatsApp messages, 4 countries by tenant currency (SA, AE, JO, GB).
 *
 * industries.content.ts, countries.content.ts and public/llms.txt repeat
 * these figures as hardcoded copy, so update them together.
 */
export const PLATFORM_STATS = {
  venues: '30+',
  locations: '60+',
  courtsAndSpaces: '250+',
  bookings: '100,000+',
  players: '40,000+',
  countries: 4,
  whatsappMessages: '200,000+',
} as const;

/**
 * ISO date stamped into JSON-LD as dateModified. AI answer engines weight
 * freshness heavily, so bump this whenever marketing copy meaningfully
 * changes (new pages, rewritten sections, updated stats) — not on
 * cosmetic tweaks, and never automatically.
 */
export const MARKETING_CONTENT_UPDATED = '2026-10-16';

export type MarketingLocale = 'en' | 'ar';

export function asMarketingLocale(language: string): MarketingLocale {
  return language === 'ar' ? 'ar' : 'en';
}

/** Absolute URL for a marketing path, e.g. marketingUrl('/faq'). */
export function marketingUrl(path: string): string {
  return `${MARKETING_SITE_URL}${path === '/' ? '' : path}`;
}
